import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import SaveAsIcon from '@mui/icons-material/SaveAs';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, TextField } from '@mui/material';

import { grey } from '@mui/material/colors';
import SmallFab from './SmallFab';
import { PromptDataModel, useSavePromptMutation } from '../../../../services/promptApi';

type SavePromptBankProps = {
	content: string;
};

const SavePromptBank: React.FC<SavePromptBankProps> = ({ content }) => {
	const [savePrompt, { isLoading: isSaving }] = useSavePromptMutation();
	const [open, setOpen] = useState(false);
	const { formatMessage } = useIntl();

	// Form state
	const [formData, setFormData] = useState<Partial<PromptDataModel>>({
		promptTitle: '',
		promptCategory: '',
		promptContent: content,
	});

	const handleInputChange = (field: keyof PromptDataModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData(prev => ({
			...prev,
			[field]: event.target.value,
		}));
	};

	const handleSavePrompt = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!content) {
			toast.error('Content on tyhjä');
			return;
		}

		const result = await savePrompt(formData);

		setOpen(false);
		if (result.error) {
			toast.error(formatMessage({ id: 'ai-prompt-bank.saved-error' }));
			return;
		}

		toast.success(formatMessage({ id: 'ai-prompt-bank.saved' }));

		// Reset form
		setFormData({
			promptTitle: '',
			promptCategory: '',
			promptContent: content,
		});
	};

	const tooltip = formatMessage({ id: 'ai-prompt-bank.save-tooltip' });

	return (
		<>
			<SmallFab tooltip={tooltip} loading={isSaving} content={<SaveAsIcon />} color={grey[50]} onClick={() => setOpen(!open)} />

			<Dialog sx={{ zIndex: 40000 }} open={open} onClose={() => setOpen(false)}>
				<DialogTitle>{formatMessage({ id: 'ai-prompt-bank.save-title' })}</DialogTitle>
				<DialogContent>
					<form onSubmit={handleSavePrompt} style={{ marginTop: '1rem' }}>
						<FormControl fullWidth sx={{ mb: '1rem' }}>
							<TextField
								variant='standard'
								label='Otsikko'
								value={formData.promptTitle}
								onChange={handleInputChange('promptTitle')}
							/>
						</FormControl>
						<FormControl fullWidth sx={{ mb: '1rem' }}>
							<TextField
								variant='standard'
								label='Kategoria'
								value={formData.promptCategory}
								onChange={handleInputChange('promptCategory')}
							/>
						</FormControl>
						<FormControl fullWidth>
							<TextField
								variant='filled'
								multiline
								rows={10}
								label='Sisältö'
								value={formData.promptContent}
								onChange={handleInputChange('promptContent')}
							/>
						</FormControl>
					</form>
				</DialogContent>
				<DialogActions>
					<Button color='success' onClick={handleSavePrompt} endIcon={<SaveAsIcon />}>
						{formatMessage({ id: 'ai-prompt-bank.save' })}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SavePromptBank;
